import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, CalendarBlank, Confetti } from "@phosphor-icons/react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function fmt(dateStr) {
  // Format YYYY-MM-DD to "12 Mar 2026 · Thu"
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, m - 1, d);
  const dow = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][dt.getDay()];
  return `${d.toString().padStart(2, "0")} ${MONTHS[m - 1]} ${y} · ${dow}`;
}

export default function HolidayList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/holidays", { params: { year } });
      setHolidays(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const upcoming = holidays.filter((h) => h.date >= today);
  const past = holidays.filter((h) => h.date < today);

  const remove = async (h) => {
    if (!window.confirm(`Delete holiday "${h.name}" on ${h.date}?`)) return;
    try {
      await api.delete(`/holidays/${h.id}`);
      toast.success("Holiday deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white border border-[#E5E7EB]" data-testid="holiday-list">
      <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#106109]/10 text-[#106109] flex items-center justify-center">
            <Confetti size={20} weight="fill" />
          </div>
          <div>
            <div className="label-caps">Company Calendar</div>
            <div className="font-display font-bold text-xl mt-0.5">Holiday list</div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            data-testid="holiday-year-select"
            className="border border-[#E5E7EB] px-3 py-2 text-sm"
          >
            {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {isAdmin && (
            <button
              onClick={() => { setEditing(null); setShowForm(true); }}
              data-testid="add-holiday-btn"
              className="brand-btn inline-flex items-center gap-2 px-4 py-2 text-sm"
            >
              <Plus size={16} weight="bold" /> Add holiday
            </button>
          )}
        </div>
      </div>

      <div className="p-6 space-y-6">
        {loading && <div className="text-sm text-[#6B7280]">Loading holidays…</div>}

        {!loading && holidays.length === 0 && (
          <div className="border border-dashed border-[#E5E7EB] p-8 text-center" data-testid="holiday-empty">
            <CalendarBlank size={28} className="mx-auto text-[#9CA3AF] mb-2" />
            <div className="text-sm text-[#6B7280]">No holidays added for {year} yet.</div>
            {isAdmin && (
              <button
                onClick={() => { setEditing(null); setShowForm(true); }}
                className="mt-4 text-sm text-[#106109] hover:underline"
              >
                Add the first holiday
              </button>
            )}
          </div>
        )}

        {upcoming.length > 0 && (
          <section>
            <div className="label-caps text-[#106109] mb-3">Upcoming · {upcoming.length}</div>
            <ul className="divide-y divide-[#F3F4F6] border border-[#E5E7EB]">
              {upcoming.map((h) => (
                <HolidayRow key={h.id} h={h} isAdmin={isAdmin}
                            onEdit={() => { setEditing(h); setShowForm(true); }}
                            onDelete={() => remove(h)} />
              ))}
            </ul>
          </section>
        )}

        {past.length > 0 && (
          <section>
            <div className="label-caps text-[#9CA3AF] mb-3">Past · {past.length}</div>
            <ul className="divide-y divide-[#F3F4F6] border border-[#F3F4F6]">
              {past.map((h) => (
                <HolidayRow key={h.id} h={h} isAdmin={isAdmin} muted
                            onEdit={() => { setEditing(h); setShowForm(true); }}
                            onDelete={() => remove(h)} />
              ))}
            </ul>
          </section>
        )}
      </div>

      {showForm && (
        <HolidayFormModal
          holiday={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function HolidayRow({ h, isAdmin, onEdit, onDelete, muted }) {
  return (
    <li className="flex items-center gap-4 px-4 py-3" data-testid={`holiday-row-${h.date}`}>
      <div className={`min-w-[132px] text-sm font-mono font-semibold ${muted ? "text-[#9CA3AF]" : "text-[#106109]"}`}>
        {fmt(h.date)}
      </div>
      <div className="flex-1">
        <div className={`text-sm font-semibold ${muted ? "text-[#6B7280]" : "text-[#111827]"}`}>{h.name}</div>
        {h.description && <div className="text-xs text-[#6B7280] mt-0.5">{h.description}</div>}
      </div>
      {isAdmin && (
        <div className="flex items-center gap-1">
          <button onClick={onEdit} data-testid={`edit-holiday-${h.date}`}
                  className="p-2 hover:bg-[#F9FAFB] text-[#6B7280]" title="Edit">
            <PencilSimple size={16} />
          </button>
          <button onClick={onDelete} data-testid={`delete-holiday-${h.date}`}
                  className="p-2 hover:bg-[#FEF2F2] text-[#DC2626]" title="Delete">
            <Trash size={16} />
          </button>
        </div>
      )}
    </li>
  );
}

function HolidayFormModal({ holiday, onClose, onSaved }) {
  const [date, setDate] = useState(holiday?.date || "");
  const [name, setName] = useState(holiday?.name || "");
  const [description, setDescription] = useState(holiday?.description || "");
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(holiday);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { date, name: name.trim(), description: description.trim() };
      if (isEdit) {
        await api.put(`/holidays/${holiday.id}`, payload);
        toast.success("Holiday updated");
      } else {
        await api.post("/holidays", payload);
        toast.success("Holiday added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="holiday-form-modal">
      <div className="bg-white w-full max-w-md border border-[#E5E7EB] p-8">
        <div className="label-caps mb-2">Holiday</div>
        <h2 className="font-display font-black text-2xl tracking-tight mb-6">
          {isEdit ? "Edit holiday" : "Add a holiday"}
        </h2>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-caps block mb-2">Date</label>
            <input
              type="date" required value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="holiday-date"
              className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#106109]/30"
            />
          </div>
          <div>
            <label className="label-caps block mb-2">Name</label>
            <input
              required value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Independence Day"
              data-testid="holiday-name"
              className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#106109]/30"
            />
          </div>
          <div>
            <label className="label-caps block mb-2">Description (optional)</label>
            <textarea
              rows={3} value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notes for staff…"
              data-testid="holiday-description"
              className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#106109]/30"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} data-testid="holiday-cancel"
                    className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">
              Cancel
            </button>
            <button type="submit" disabled={saving} data-testid="holiday-save"
                    className="brand-btn flex-1 py-2.5 text-sm">
              {saving ? "Saving…" : (isEdit ? "Update holiday" : "Add holiday")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
